import { useState, type FormEvent } from "react";
import { changeCoMemberPassword, createCoMember, ApiError } from "../../services/api";
import { PasswordInput } from "../../components/PasswordInput";

/** FR-044: self-service password change, plus creating additional CO Member accounts (the only
 * way any CO Member account after the first one gets created — there's no public
 * self-registration route). */
export function CoMemberSettingsPage() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: "success" | "error"; text: string } | null>(
    null,
  );

  const [newFullName, setNewFullName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newAccountPassword, setNewAccountPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  async function handlePasswordChange(e: FormEvent) {
    e.preventDefault();
    setPasswordSubmitting(true);
    setPasswordMessage(null);
    try {
      await changeCoMemberPassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setPasswordMessage({ type: "success", text: "Password changed." });
    } catch (err) {
      const text =
        err instanceof ApiError && err.status === 401
          ? "Current password is incorrect."
          : "Something went wrong. Please try again.";
      setPasswordMessage({ type: "error", text });
    } finally {
      setPasswordSubmitting(false);
    }
  }

  async function handleCreateAccount(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateMessage(null);
    try {
      await createCoMember(newFullName, newEmail, newAccountPassword);
      setNewFullName("");
      setNewEmail("");
      setNewAccountPassword("");
      setCreateMessage({ type: "success", text: "CO Member account created." });
    } catch (err) {
      const text =
        err instanceof ApiError && err.status === 400
          ? "That email is already in use."
          : "Something went wrong. Please try again.";
      setCreateMessage({ type: "error", text });
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="mx-auto max-w-md p-6">
      <h1 className="mb-6 font-sans text-xl text-ink">Settings</h1>

      <section className="mb-8">
        <h2 className="mb-3 font-sans text-lg text-ink">Change Password</h2>
        <form onSubmit={handlePasswordChange} className="flex flex-col gap-3">
          <PasswordInput
            label="Current password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <PasswordInput
            label="New password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          {passwordMessage && (
            <p className={`text-sm ${passwordMessage.type === "success" ? "text-brand" : "text-accent"}`}>
              {passwordMessage.text}
            </p>
          )}
          <button
            type="submit"
            disabled={passwordSubmitting}
            className="self-start rounded bg-brand px-4 py-2 font-sans text-surface disabled:opacity-50"
          >
            {passwordSubmitting ? "Saving…" : "Change password"}
          </button>
        </form>
      </section>

      <section>
        <h2 className="mb-3 font-sans text-lg text-ink">Add a CO Member</h2>
        <form onSubmit={handleCreateAccount} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm text-body">
            Full name
            <input
              required
              value={newFullName}
              onChange={(e) => setNewFullName(e.target.value)}
              className="rounded border border-surface-alt px-3 py-2"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-body">
            Email
            <input
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="rounded border border-surface-alt px-3 py-2"
            />
          </label>
          <PasswordInput
            label="Initial password"
            required
            value={newAccountPassword}
            onChange={(e) => setNewAccountPassword(e.target.value)}
          />
          {createMessage && (
            <p className={`text-sm ${createMessage.type === "success" ? "text-brand" : "text-accent"}`}>
              {createMessage.text}
            </p>
          )}
          <button
            type="submit"
            disabled={creating}
            className="self-start rounded bg-brand px-4 py-2 font-sans text-surface disabled:opacity-50"
          >
            {creating ? "Creating…" : "Create account"}
          </button>
        </form>
      </section>
    </div>
  );
}
